import { Task } from '../../../../types';

export interface CampaignUserTaskStats {
  /** `claimed`. Tasks held by a worker but not opened into active work yet. */
  claimed: number;
  /** `in_progress`. Tasks the worker has opened/started. */
  inProgress: number;
  /** `changes_requested`. Reviewer kicked it back; broken out from `claimed`
   *  so admins can spot workers with a backlog of rework. */
  rework: number;
  inReview: number;
  completed: number;
}

function emptyStats(): CampaignUserTaskStats {
  return {
    claimed: 0,
    inProgress: 0,
    rework: 0,
    inReview: 0,
    completed: 0,
  };
}

export function totalCampaignUserTaskStats(stats: CampaignUserTaskStats): number {
  return stats.claimed + stats.inProgress + stats.rework + stats.inReview + stats.completed;
}

/**
 * Summarize campaign tasks by owner for the admin campaign drill-down.
 * Mirrors the progress bar semantics:
 *   - claimed:   claimed
 *   - in progress: in_progress
 *   - rework:    changes_requested
 *   - in review: in_review
 *   - completed: approved
 */
export function buildCampaignUserTaskStats(tasks: Task[]): Map<string, CampaignUserTaskStats> {
  const statsByUid = new Map<string, CampaignUserTaskStats>();

  for (const task of tasks) {
    if (!task.ownerId) continue;
    let stats = statsByUid.get(task.ownerId);
    if (!stats) {
      stats = emptyStats();
      statsByUid.set(task.ownerId, stats);
    }

    if (task.status === 'claimed') {
      stats.claimed += 1;
      continue;
    }
    if (task.status === 'in_progress') {
      stats.inProgress += 1;
      continue;
    }
    if (task.status === 'changes_requested') {
      stats.rework += 1;
      continue;
    }
    if (task.status === 'in_review') {
      stats.inReview += 1;
      continue;
    }
    if (task.status === 'approved') {
      stats.completed += 1;
    }
  }

  return statsByUid;
}
